import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Drawer } from "@sylviq/ui-react-web/components/ui/drawer";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { Select } from "@sylviq/ui-react-web/components/ui/select";
import { ArrowDown, ArrowUp, Copy, Plus, RotateCcw, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import {
  activePreset,
  addPreset,
  type AnyDataSource,
  type DashboardState,
  moveWidget,
  newId,
  type Preset,
  removePreset,
  replacePreset,
  SPAN_LABELS,
  SPANS,
  type Span,
  stateFromDefaults,
  type WidgetConfig,
} from "./model.js";
import { resolveOptions, type WidgetDefinition } from "./widgets.js";

export interface DashboardBuilderProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  state: DashboardState;
  defaults: Preset[];
  sources: AnyDataSource[];
  widgetTypes: WidgetDefinition[];
  onSave: (state: DashboardState) => void;
  onSaveWorkspaceDefault?: (state: DashboardState) => void;
}

/**
 * Edits a copy of the dashboard state and commits it on Save, so backing out
 * with Cancel or Escape never leaves a half-edited layout on the page.
 */
export function DashboardBuilder({
  open,
  onOpenChange,
  state,
  defaults,
  sources,
  widgetTypes,
  onSave,
  onSaveWorkspaceDefault,
}: DashboardBuilderProps) {
  const [draft, setDraft] = useState(state);
  const [adding, setAdding] = useState("");

  useEffect(() => {
    if (open) {
      setDraft(state);
      setAdding("");
    }
  }, [open, state]);

  const preset = activePreset(draft);
  const setWidgets = (widgets: WidgetConfig[]) =>
    setDraft(replacePreset(draft, { ...preset, widgets }));
  const patchWidget = (index: number, patch: Partial<WidgetConfig>) =>
    setWidgets(preset.widgets.map((w, i) => (i === index ? { ...w, ...patch } : w)));

  // The widget type follows the source: a table source gets the table
  // widget. When several types read the same kind, the first registered wins.
  const definitionFor = (source: AnyDataSource) =>
    widgetTypes.find((definition) => definition.kind === source.kind);

  const addWidget = (sourceId: string) => {
    const source = sources.find((s) => s.id === sourceId);
    const definition = source ? definitionFor(source) : undefined;
    if (!source || !definition) return;
    setWidgets([
      ...preset.widgets,
      {
        id: newId("w"),
        type: definition.type,
        source: source.id,
        span: definition.defaultSpan,
      },
    ]);
    setAdding("");
  };

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title="Customize overview"
      description="Choose what data drives each block and how it is laid out. Save several layouts and switch between them from the overview."
      className="w-[min(36rem,100%)]"
      footer={
        <>
          <Button type="button" onClick={() => onSave(draft)}>
            Save
          </Button>
          {onSaveWorkspaceDefault ? (
            <Button type="button" variant="outline" onClick={() => onSaveWorkspaceDefault(draft)}>
              Save as project default
            </Button>
          ) : null}
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
        </>
      }
    >
      <div className="grid gap-6">
        <section className="grid gap-2" aria-label="Layout">
          <label className="grid gap-1 text-xs font-medium text-[var(--muted-foreground)]">
            Layout
            <Select
              value={draft.activeId}
              options={draft.presets.map((p) => ({ value: p.id, label: p.name }))}
              onChange={(activeId) => activeId && setDraft({ ...draft, activeId })}
            />
          </label>
          <label className="grid gap-1 text-xs font-medium text-[var(--muted-foreground)]">
            Layout name
            <Input
              value={preset.name}
              onChange={(event) =>
                setDraft(replacePreset(draft, { ...preset, name: event.target.value }))
              }
            />
          </label>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setDraft(addPreset(draft, `${preset.name} copy`, preset))}
            >
              <Copy className="size-3.5" /> Duplicate
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setDraft(addPreset(draft, "New layout"))}
            >
              <Plus className="size-3.5" /> New blank layout
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={draft.presets.length <= 1}
              onClick={() => setDraft(removePreset(draft, preset.id))}
            >
              <Trash2 className="size-3.5" /> Delete layout
            </Button>
          </div>
        </section>

        <section className="grid gap-3" aria-label="Blocks">
          <h3 className="text-sm font-semibold">Blocks</h3>
          {preset.widgets.length === 0 ? (
            <p className="text-sm text-[var(--muted-foreground)]">
              No blocks yet — add one below.
            </p>
          ) : null}
          <ol className="grid gap-3">
            {preset.widgets.map((widget, index) => {
              const definition = widgetTypes.find((d) => d.type === widget.type);
              const compatible = sources.filter(
                (s) =>
                  s.kind === definition?.kind &&
                  (s.offered !== false || s.id === widget.source),
              );
              const source = sources.find((s) => s.id === widget.source);
              return (
                <li key={widget.id} className="surface grid gap-3 rounded-xl p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-medium text-[var(--muted-foreground)]">
                      {definition?.label ?? widget.type}
                    </span>
                    <span className="flex gap-1">
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        aria-label="Move up"
                        disabled={index === 0}
                        onClick={() => setWidgets(moveWidget(preset.widgets, index, -1))}
                      >
                        <ArrowUp className="size-4" />
                      </Button>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        aria-label="Move down"
                        disabled={index === preset.widgets.length - 1}
                        onClick={() => setWidgets(moveWidget(preset.widgets, index, 1))}
                      >
                        <ArrowDown className="size-4" />
                      </Button>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        aria-label="Remove block"
                        onClick={() => setWidgets(preset.widgets.filter((_, i) => i !== index))}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </span>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                  <label className="grid gap-1 text-xs font-medium text-[var(--muted-foreground)]">
                    Data
                    <Select
                      value={source ? widget.source : ""}
                      placeholder="Choose data…"
                      options={compatible.map((s) => ({ value: s.id, label: s.label }))}
                      onChange={(id) => id && patchWidget(index, { source: id })}
                    />
                  </label>
                  <label className="grid gap-1 text-xs font-medium text-[var(--muted-foreground)]">
                    Width
                    <Select
                      value={String(widget.span)}
                      options={SPANS.map((span) => ({
                        value: String(span),
                        label: SPAN_LABELS[span],
                      }))}
                      onChange={(value) => patchWidget(index, { span: Number(value) as Span })}
                    />
                  </label>
                  </div>
                  <label className="grid gap-1 text-xs font-medium text-[var(--muted-foreground)]">
                    Title
                    <Input
                      value={widget.title ?? ""}
                      placeholder={source?.label ?? "Block title"}
                      onChange={(event) =>
                        patchWidget(index, { title: event.target.value || undefined })
                      }
                    />
                  </label>
                  {definition?.options?.map((field) => {
                    const current = resolveOptions(definition, widget)[field.key];
                    const setOption = (value: string | number | boolean) =>
                      patchWidget(index, { options: { ...widget.options, [field.key]: value } });
                    return (
                      <label
                        key={field.key}
                        className="grid gap-1 text-xs font-medium text-[var(--muted-foreground)]"
                      >
                        {field.type === "boolean" ? (
                          <span className="flex items-center gap-2 text-sm text-[var(--foreground)]">
                            <input
                              type="checkbox"
                              checked={Boolean(current)}
                              onChange={(event) => setOption(event.target.checked)}
                            />
                            {field.label}
                          </span>
                        ) : (
                          <>
                            {field.label}
                            {field.type === "select" ? (
                              <Select
                                value={String(current)}
                                options={field.choices}
                                onChange={setOption}
                              />
                            ) : (
                              <Input
                                type="number"
                                min={field.min}
                                max={field.max}
                                value={Number(current)}
                                onChange={(event) =>
                                  setOption(
                                    Math.min(
                                      field.max,
                                      Math.max(field.min, Number(event.target.value) || field.default),
                                    ),
                                  )
                                }
                              />
                            )}
                          </>
                        )}
                      </label>
                    );
                  })}
                </li>
              );
            })}
          </ol>
          <label className="grid gap-1 text-xs font-medium text-[var(--muted-foreground)]">
            Add a block
            <Select
              value={adding}
              placeholder="Choose data to add…"
              options={sources
                .filter((s) => s.offered !== false)
                .map((s) => ({ value: s.id, label: s.label }))}
              onChange={(id) => id && addWidget(id)}
            />
          </label>
        </section>

        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="justify-self-start"
          onClick={() => setDraft(stateFromDefaults(defaults, draft.activeId))}
        >
          <RotateCcw className="size-3.5" /> Reset to the built-in layouts
        </Button>
      </div>
    </Drawer>
  );
}
